/* AO · rail engine — slow continuous marquee + manual photo / film rails, merchant-controlled.
   Contract (markup, schema, CSS): finalpass-20260915/contracts/RAIL-CONTRACT.md
   Backward compatible: rails that only carry the original data-ao-marquee-* attributes (Oil ao-vnext-ugc and
   Protocol ao-ugc-rail film rails) keep their speed, direction, loop, pauses, clone markup and events.

   Rail attributes (set from the theme editor, see snippets/ao-rail-attrs.liquid):
     data-ao-marquee                 enable auto movement (needs >= 2 cards; never for reduced-motion users)
     data-ao-marquee-speed="24"      px per second (very slow 12 · slow 24 · medium 40 · fast 64)
     data-ao-marquee-direction       "left" (content moves leftwards, default) | "right"
     data-ao-marquee-loop            "true" seamless loop (default) | "false" (run to the end and stop)
     data-ao-marquee-hover           "true" pause on mouse hover / keyboard focus
     data-ao-marquee-interaction     "true" pause while swiping, dragging or wheel-scrolling, then resume
     data-ao-marquee-drag            "true" mouse drag scrolls the rail on desktop (touch keeps native swipe)
     data-ao-marquee-clone-inert     "true" clones are inert (photo rails); absent = cloned films stay tappable
     data-ao-rail                    contract rail: arrows, count, drag, editor select even without motion
     data-ao-rail-kind               "photo" | "video" (informational)
     data-ao-rail-count              real card count, clones excluded (kept current by the engine)
   Engine output: .ao-marquee (motion engine active) · .ao-marquee--fits (all cards fit: static, no clones)
     · .ao-marquee--static (reduced motion) · .ao-rail--dragging; on [data-ao-rail] rails also
     data-ao-rail-overflow="true|false", data-ao-rail-edge="none|start|end|both", data-ao-rail-motion.
   Controls live outside the rail and are bound by the rail id:
     [data-ao-rail-prev] / [data-ao-rail-next] / [data-ao-rail-pause] buttons with aria-controls="RAIL_ID";
     counter [data-ao-rail-for="RAIL_ID"] holding [data-ao-rail-index] / [data-ao-rail-total];
     polite status [data-ao-rail-status][data-ao-rail-for="RAIL_ID"] (filled after arrow navigation);
     their [data-ao-rail-ui] container gets data-ao-rail-overflow / data-ao-rail-motion too (hide controls when all fit).
   Events: ao:marquee:cloned {nodes} · ao:marquee:uncloned {nodes} · ao:marquee:ready {count, loopWidth, mode}
     · ao:rail:change {index, total}; listens for ao:marquee:hold (hold, then resume).
   Films always pause the rail while they play (a film removed from the rail while it plays is paused and releases the
   hold; the settle wrap waits while a film plays, so no loop jump moves it out of view, while the physical-edge wrap
   and the loop centring still run there); the rail also stops when the tab is hidden or the rail is
   off-screen. Theme Editor: section select holds, deselect releases; block select scrolls the real card into
   view (static rails too; clones never carry editor attributes); shopify:section:unload releases every
   listener, observer, timer and frame of the old rail. */
(function () {
  'use strict';
  if (window.__aoUgcMarquee) return;
  window.__aoUgcMarquee = true;

  var RAILS = '[data-ao-marquee], [data-ao-rail]';
  var MAX_SETS = 8;
  var FOCUSABLE = 'a, button, video, audio, iframe, input, select, textarea, summary, [tabindex], [contenteditable]';
  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var finePointer = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;
  var controllers = typeof WeakMap === 'function' ? new WeakMap() : null;
  var live = [];
  var keyboardModality = false;
  var smoothOK = 'scrollBehavior' in document.documentElement.style;

  document.addEventListener('keydown', function (e) { if (!e.metaKey && !e.ctrlKey && !e.altKey) keyboardModality = true; }, true);
  document.addEventListener('pointerdown', function () { keyboardModality = false; }, true);

  function flag(rail, name, fallback) {
    var v = rail.getAttribute('data-ao-marquee-' + name);
    if (v === null || v === '') return fallback;
    return v !== 'false';
  }
  function num(v) { var n = parseFloat(v); return isFinite(n) ? n : 0; }
  function reduced() { return !!(reduce && reduce.matches); }
  function keyboardFocus(el) {
    if (!el || el.nodeType !== 1) return false;
    try { return el.matches(':focus-visible'); } catch (e) { return keyboardModality; }
  }
  function emit(el, name, detail) {
    try { el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail })); } catch (e) { /* older browsers */ }
  }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function setAttr(el, name, value) { if (el.getAttribute(name) !== value) el.setAttribute(name, value); }
  function setText(el, value) { if (el.textContent !== value) el.textContent = value; }
  function toArray(list) { return Array.prototype.slice.call(list); }

  function Controller(rail) {
    var self = this;
    self.rail = rail;

    var motionAttr = rail.hasAttribute('data-ao-marquee');
    var contract = rail.hasAttribute('data-ao-rail');
    var speed = parseFloat(rail.getAttribute('data-ao-marquee-speed')) || 24;
    var dir = rail.getAttribute('data-ao-marquee-direction') === 'right' ? -1 : 1;
    var loop = flag(rail, 'loop', true);
    var pauseHover = flag(rail, 'hover', true);
    var pauseInteraction = flag(rail, 'interaction', true);
    var drag = rail.getAttribute('data-ao-marquee-drag') === 'true';
    var cloneInert = rail.getAttribute('data-ao-marquee-clone-inert') === 'true';

    var listeners = [];
    function on(target, type, fn, options) {
      target.addEventListener(type, fn, options || false);
      listeners.push([target, type, fn, options || false]);
    }

    var cards = [];      // original cards: element children of the rail that are not clones
    var items = [];      // laid-out originals: { el, i, start, end } in scroll-content coordinates
    var sets = [];       // clone sets, each aligned with cards
    var stops = [];      // sorted scrollLeft values that align a card (original or clone) with the rail inset
    var loopWidth = 0, inset = 0, mode = 'manual', fits = false, seated = false, readySent = false;
    var pos = 0, last = 0, raf = 0, running = false, visible = false, destroyed = false;
    var hover = false, focused = false, controlFocused = false, pointerHold = false, touchHold = false;
    var playing = false, userHold = false, blockHold = false, sectionHold = false, userPaused = false, finished = false;
    var dragging = false, suppressClick = false, pendingTarget = null, lastIndex = -1, announce = false;
    var navigating = false; // an arrow / external smooth scroll is in flight: only the idle timer may settle (wrap)
    var endIndex = null;    // non-loop rails parked at the physical end: the card the arrows stepped to (null = last card)
    var idleTimer = 0, settleTimer = 0, layoutFrame = 0, uiFrame = 0, legacyMq = false, flips = [];
    var io = null, ro = null;
    var controls = { prev: [], next: [], pause: [], counters: [], status: [], ui: [] };

    function originals() {
      return toArray(rail.children).filter(function (el) {
        return !el.hasAttribute('data-ao-marquee-clone') && !/^(SCRIPT|STYLE|TEMPLATE|LINK)$/.test(el.tagName);
      });
    }
    function sameCards(list) {
      if (list.length !== cards.length) return false;
      for (var i = 0; i < list.length; i++) { if (list[i] !== cards[i]) return false; }
      return true;
    }
    function maxScroll() { return Math.max(0, rail.scrollWidth - rail.clientWidth); }
    function canMove() { return motionAttr && !reduced() && cards.length >= 2; }
    // Loop window [0.5 · loopWidth, 1.5 · loopWidth): visually identical positions, swipeable both ways.
    function wrap(p) {
      if (!(loopWidth > 0)) return p;
      var lo = loopWidth / 2;
      return lo + ((((p - lo) % loopWidth) + loopWidth) % loopWidth);
    }

    /* ---------- clones ---------- */
    function makeClone(card) {
      var clone = card.cloneNode(true);
      clone.setAttribute('data-ao-marquee-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');
      clone.removeAttribute('id');
      clone.querySelectorAll('[data-shopify-editor-block]').forEach(function (n) { n.removeAttribute('data-shopify-editor-block'); });
      clone.querySelectorAll('[id]').forEach(function (n) { n.removeAttribute('id'); });
      clone.querySelectorAll(FOCUSABLE).forEach(function (n) { n.setAttribute('tabindex', '-1'); });
      if (cloneInert) { clone.setAttribute('inert', ''); try { clone.inert = true; } catch (e) { /* no inert support */ } }
      return clone;
    }
    function addSet() {
      var frag = document.createDocumentFragment();
      var set = cards.map(function (card) { var c = makeClone(card); frag.appendChild(c); return c; });
      rail.appendChild(frag);
      sets.push(set);
      return set;
    }
    function removeSets(keep) {
      var removed = [];
      while (sets.length > keep) {
        sets.pop().forEach(function (n) {
          // A removed clone keeps playing detached, and its pause event never reaches the rail: stop it here.
          n.querySelectorAll('video').forEach(function (v) {
            if (!v.paused) { try { v.pause(); } catch (e) { /* ignore */ } }
          });
          if (n.parentNode === rail) rail.removeChild(n);
          removed.push(n);
        });
      }
      if (removed.length) syncPlaying();
      return removed;
    }
    var dropEnd = null; // a loop -> static switch parked at the physical end: the card that was at the inset (layout applies it)
    function dropClones() {
      if (!sets.length) return;
      // An arrow smooth scroll in flight already counts its target card: land on that card, not half way.
      var p = pendingTarget !== null ? pendingTarget : rail.scrollLeft;
      var L = loopWidth;
      var shown = mode === 'loop' && items.length ? nearestIndex(p) : -1;
      stop();
      emit(rail, 'ao:marquee:uncloned', { nodes: removeSets(0) });
      // The instant scroll below replaces any smooth scroll in flight.
      pendingTarget = null;
      navigating = false;
      dropEnd = null;
      var mx = maxScroll();
      if (L > 0) p = ((p % L) + L) % L; // the visually identical position within the original cards
      if (p > mx) {
        // Past the end of the original cards (e.g. a live switch to reduced motion mid-loop): park at the end and keep
        // counting the card that was shown, or go back to the start when the loop was already bringing card 1 in.
        if (shown === 0) { p = 0; } else { p = mx; if (shown > 0) dropEnd = shown; }
      }
      rail.scrollLeft = p;
      pos = rail.scrollLeft;
    }
    function ensureSets() {
      var need = loopWidth * 1.5 + num(window.getComputedStyle(rail).paddingRight);
      var added = [];
      if (!sets.length) added = added.concat(addSet());
      while (maxScroll() < need && sets.length < MAX_SETS) added = added.concat(addSet());
      var removed = [];
      while (sets.length > 1 && maxScroll() - loopWidth >= need && rail.scrollLeft <= maxScroll() - loopWidth) {
        removed = removed.concat(removeSets(sets.length - 1));
      }
      if (added.length) emit(rail, 'ao:marquee:cloned', { nodes: added });
      if (removed.length) emit(rail, 'ao:marquee:uncloned', { nodes: removed });
    }

    /* ---------- measurement ---------- */
    function measure() {
      var cs = window.getComputedStyle(rail);
      var sp = cs.scrollPaddingLeft;
      inset = sp && sp !== 'auto' && num(sp) > 0 ? num(sp) : num(cs.paddingLeft);
      var railRect = rail.getBoundingClientRect();
      var originX = railRect.left + rail.clientLeft - rail.scrollLeft;
      var track = true, prevEnd = -Infinity;
      items = [];
      cards.forEach(function (card, i) {
        var r = card.getBoundingClientRect();
        if (!r.width && !r.height) return;
        var start = r.left - originX;
        if (start < prevEnd - 1) track = false; // wrapped rows or a grid: not a horizontal track
        prevEnd = r.right - originX;
        items.push({ el: card, i: i, start: start, end: prevEnd });
      });
      if (!items.length) { loopWidth = 0; return { track: false, width: 0, overflow: 0 }; }
      var first = items[0], lastItem = items[items.length - 1];
      var lastCs = window.getComputedStyle(lastItem.el);
      var span = (lastItem.end + num(lastCs.marginRight)) - (first.start - num(window.getComputedStyle(first.el).marginLeft));
      var width = span + num(cs.columnGap);
      if (sets.length && sets[0][first.i]) {
        var tr = sets[0][first.i].getBoundingClientRect();
        if (tr.width || tr.height) {
          var measured = tr.left - originX - first.start;
          if (measured > span - 1) { width = measured; } else { track = false; }
        }
      }
      loopWidth = track ? width : 0;
      var contentEnd = lastItem.end + num(lastCs.marginRight) + num(cs.paddingRight);
      return { track: track, width: width, overflow: contentEnd - rail.clientWidth };
    }
    function computeStops() {
      stops = [];
      var copies = mode === 'loop' ? sets.length + 1 : 1;
      for (var k = 0; k < copies; k++) {
        for (var i = 0; i < items.length; i++) stops.push(items[i].start + k * loopWidth - inset);
      }
      stops.sort(function (a, b) { return a - b; });
    }

    /* ---------- layout ---------- */
    function scheduleLayout() { if (!layoutFrame && !destroyed) layoutFrame = requestAnimationFrame(layout); }
    function layout() {
      layoutFrame = 0;
      if (destroyed) return;
      var list = originals();
      if (!sameCards(list)) {
        var gone = removeSets(0);
        if (gone.length) emit(rail, 'ao:marquee:uncloned', { nodes: gone });
        if (ro) {
          cards.forEach(function (c) { ro.unobserve(c); });
          list.forEach(function (c) { ro.observe(c); });
        }
        cards = list;
        mode = 'manual';
        endIndex = null;
        syncPlaying(); // a film card removed while it played
      }
      var moving = canMove();
      rail.classList.toggle('ao-marquee--static', motionAttr && reduced());
      rail.classList.toggle('ao-marquee', moving);
      if (!rail.clientWidth || !cards.length) { stop(); scheduleUI(); return; }

      var m = measure();
      var previous = mode;
      if (moving && loop) {
        var nextFits = !m.track || m.width <= rail.clientWidth + 0.5 || m.overflow <= 1;
        if (nextFits !== fits && previous !== 'manual') {
          var now = Date.now();
          flips = flips.filter(function (t) { return now - t < 1000; });
          flips.push(now);
          if (flips.length > 6) nextFits = true; // a stylesheet toggles layout with the engine classes: stay static
        }
        fits = nextFits;
        if (fits) {
          dropClones();
          mode = 'fits';
        } else {
          mode = 'loop';
          ensureSets();
          if (previous !== 'loop') {
            // first run: scrollLeft 0 -> loopWidth. A held rail (e.g. reduced motion switched off while a film plays)
            // keeps its position, so the film stays in view; start() wraps once the hold ends.
            if (holdWrap() || playing) { pos = rail.scrollLeft; } else { pos = wrap(rail.scrollLeft); rail.scrollLeft = pos; }
          } else if (!running && !touchHold && !dragging && !navigating && pendingTarget === null && !holdWrap() && !playing) {
            settleWrap();
          }
        }
      } else {
        dropClones();
        fits = m.overflow <= 1;
        mode = moving ? 'run' : 'manual';
        if (moving && !seated) {
          seated = true;
          if (dir < 0) { pos = maxScroll(); rail.scrollLeft = pos; }
        }
      }
      rail.classList.toggle('ao-marquee--fits', moving && fits);
      if (mode !== previous) {
        endIndex = null;
        pendingTarget = null; // the mode switch scrolled instantly: any smooth scroll in flight was cancelled
        navigating = false;
      }
      if (dropEnd !== null) {
        // loop -> static, parked at the physical end: the counter keeps the card that was shown; the end steps continue from it
        if (mode === 'manual' || mode === 'run') endIndex = Math.max(nearestIndex(maxScroll()), Math.min(items.length - 1, dropEnd));
        dropEnd = null;
      }
      computeStops();
      scheduleUI();
      if (!readySent) {
        readySent = true;
        emit(rail, 'ao:marquee:ready', { count: items.length, loopWidth: mode === 'loop' ? loopWidth : 0, mode: mode });
      }
      if (mode === 'loop' || mode === 'run') { start(); } else { stop(); }
    }

    /* ---------- motion ---------- */
    function wantRun() {
      return (mode === 'loop' || mode === 'run') && visible && !hover && !focused && !controlFocused &&
        !pointerHold && !touchHold && !dragging && !playing && !userHold && !blockHold && !sectionHold &&
        !userPaused && !finished && !document.hidden;
    }
    function step(ts) {
      raf = 0;
      if (!wantRun()) { running = false; return; }
      if (!last) last = ts;
      var dt = Math.min(64, ts - last);
      last = ts;
      pos += dir * speed * dt / 1000;
      if (mode === 'loop') {
        pos = wrap(pos);
      } else {
        var mx = maxScroll();
        if (pos >= mx) { pos = mx; finished = true; }
        if (pos <= 0) { pos = 0; if (dir < 0) finished = true; }
      }
      rail.scrollLeft = pos;
      raf = requestAnimationFrame(step);
    }
    function start() {
      if (destroyed || running || !wantRun()) return;
      running = true;
      last = 0;
      pendingTarget = null;
      navigating = false;
      pos = rail.scrollLeft;
      if (mode === 'loop') {
        var w = wrap(pos);
        if (Math.abs(w - pos) >= 0.5) { pos = w; rail.scrollLeft = w; }
      }
      raf = requestAnimationFrame(step);
    }
    function stop() { running = false; if (raf) cancelAnimationFrame(raf); raf = 0; }
    function holdThenResume(ms) {
      userHold = true;
      clearTimeout(idleTimer);
      stop();
      idleTimer = setTimeout(function () { userHold = false; start(); }, ms || 2600);
    }
    function anyPlaying() {
      return Array.prototype.some.call(rail.querySelectorAll('video'), function (v) { return !v.paused && !v.ended; });
    }
    // A film that leaves the rail while it plays (clone sets dropped for reduced motion or a card change, a block removed
    // in the editor) fires its pause event on a detached node: re-read what still plays, or the rail stays held forever.
    function syncPlaying() {
      if (!playing || destroyed) return;
      playing = anyPlaying();
      if (!playing) holdThenResume(1200);
    }

    /* ---------- idle scrolling (swipe, wheel, arrows, drag) ---------- */
    function keyboardFocusInside() {
      var a = document.activeElement;
      return !!(a && a !== rail && rail.contains(a) && keyboardFocus(a));
    }
    // No loop jump while the editor holds a block or keyboard focus is inside: it would move that card out of view.
    function holdWrap() { return blockHold || keyboardFocusInside(); }
    // A playing film defers the settle wrap only (first loop layout and onSettle), so no jump moves the film out of
    // view while it plays. The physical-edge wrap and centreLoop still run while a film plays: a manual scroll must
    // never clamp at an edge and an arrow's scrollBy must never be cut short there. start() wraps once the film pauses.
    function settleWrap() {
      if (mode !== 'loop' || !(loopWidth > 0)) return;
      var p = rail.scrollLeft;
      var w = wrap(p);
      if (Math.abs(w - p) >= 0.5) rail.scrollLeft = w;
      pos = rail.scrollLeft;
    }
    // External navigation (e.g. a section's own arrows dispatching ao:marquee:hold before scrollBy): move to the
    // visually identical loop position with the most room on both sides, so a smooth scroll never clamps at an edge.
    function centreLoop() {
      if (mode !== 'loop' || !(loopWidth > 0) || running || touchHold || dragging || settleTimer || pendingTarget !== null || holdWrap()) return;
      var p = rail.scrollLeft;
      var mx = maxScroll();
      var best = p, room = Math.min(p, mx - p);
      [p - loopWidth, p + loopWidth].forEach(function (q) {
        if (q < 0 || q > mx) return;
        var r = Math.min(q, mx - q);
        if (r > room + 0.5) { best = q; room = r; }
      });
      if (best !== p) {
        // The jump's own scrollend must not wrap back mid-way through the caller's smooth scroll.
        navigating = true;
        rail.scrollLeft = best;
        pos = rail.scrollLeft;
        scheduleSettle();
      }
    }
    function onSettle() {
      settleTimer = 0;
      if (destroyed || running) return;
      if (touchHold || dragging) return; // touchend / drag end schedule another settle
      pendingTarget = null;
      navigating = false;
      if (!holdWrap() && !playing) settleWrap();
      flushAnnounce();
      scheduleUI();
    }
    function scheduleSettle() { clearTimeout(settleTimer); settleTimer = setTimeout(onSettle, 140); }
    function onScroll() {
      if (endIndex !== null && !navigating && rail.scrollLeft < maxScroll() - 1) endIndex = null; // left the end by hand
      scheduleUI();
      if (running) return;
      pos = rail.scrollLeft;
      if (mode === 'run') finished = false;
      scheduleSettle();
      if (mode !== 'loop' || !(loopWidth > 0) || navigating || holdWrap()) return;
      var mx = maxScroll();
      if (pos <= 1 || pos >= mx - 1) { pendingTarget = null; settleWrap(); } // never let a swipe clamp at an edge
    }

    /* ---------- arrows, counter, status, pause ---------- */
    function nextStop(cur, d) {
      var t = null;
      for (var i = 0; i < stops.length; i++) {
        if (d > 0 && stops[i] > cur + 2) { t = stops[i]; break; }
        if (d < 0 && stops[i] < cur - 2) t = stops[i];
      }
      return t;
    }
    // Loop rails: the stop of the card one step from the card the counter shows, in the direction of travel. Motion or
    // a swipe can leave the rail past the midpoint towards the next card, where the nearest stop is the card already
    // counted, so every press advances exactly one card from currentIndex() instead of snapping to that stop.
    function loopStop(cur, d) {
      var n = items.length;
      var c = (((currentIndex(cur, maxScroll()) + d) % n) + n) % n;
      var b = items[c].start - inset;
      var k = d > 0 ? Math.floor((cur + 2 - b) / loopWidth) + 1 : Math.ceil((cur - 2 - b) / loopWidth) - 1;
      return b + k * loopWidth;
    }
    // Manual / static rails: the stop of the card one step from the card the counter shows. At the physical end the
    // counter can rest on a card that sits partly past the inset, whose own stop is the last stop before the end, so a
    // prev press from there only aligned that card. No card left in that direction, or a stop that would not move in the
    // direction of travel, keeps the edge scroll of nextStop() (next still disables at the last card, prev at the first).
    function manualStop(cur, mx, d) {
      var c = currentIndex(cur, mx) + d;
      if (c < 0 || c > items.length - 1) return nextStop(cur, d);
      var t = items[c].start - inset;
      return (d > 0 ? t > cur + 2 : t < cur - 2) ? t : nextStop(cur, d);
    }
    function go(d) {
      if (destroyed) return;
      if (!stops.length) { measure(); computeStops(); }
      var mx = maxScroll();
      if (mode !== 'loop' && mx <= 1) return;
      var cur = pendingTarget !== null ? pendingTarget : rail.scrollLeft;
      var manualSteps = mode !== 'loop' && items.length > 0;
      if (manualSteps && cur >= mx - 1) {
        // Parked at the physical end, where the last cards are already visible: step the counter one card per press
        // (no scroll) up to the last card; prev scrolls again once the counter is back on the card nearest the end.
        var stepTo = currentIndex(cur, mx) + d;
        if (d > 0 ? stepTo <= items.length - 1 : stepTo >= nearestIndex(mx)) {
          endIndex = stepTo;
          holdThenResume(3200);
          announce = true;
          if (pendingTarget === null) flushAnnounce();
          scheduleUI();
          return;
        }
      }
      if (mode !== 'loop' && ((d > 0 && cur >= mx - 1) || (d < 0 && cur <= 1))) return;
      var from = manualSteps ? currentIndex(cur, mx) : 0;
      var loopSteps = mode === 'loop' && loopWidth > 0 && items.length > 0;
      var target = loopSteps ? loopStop(cur, d) : manualSteps ? manualStop(cur, mx, d) : nextStop(cur, d);
      navigating = true;
      if (mode === 'loop' && loopWidth > 0 && (target === null || target < 1 || target > mx - 1)) {
        var shift = d > 0 ? -loopWidth : loopWidth;
        rail.scrollLeft = rail.scrollLeft + shift; // visually identical position one loop away
        pos = rail.scrollLeft;
        cur += shift;
        target = loopSteps ? target + shift : nextStop(cur, d);
      }
      if (target === null) target = d > 0 ? mx : 0;
      target = Math.max(0, Math.min(mx, target));
      // A next press whose stop lies beyond the end counts the card it advanced to, not the last card.
      if (manualSteps) endIndex = d > 0 && target >= mx - 1 ? Math.min(items.length - 1, Math.max(from + 1, nearestIndex(mx))) : null;
      holdThenResume(3200);
      announce = true;
      if (Math.abs(target - rail.scrollLeft) < 1) { pendingTarget = null; navigating = false; flushAnnounce(); scheduleUI(); return; }
      pendingTarget = target;
      if (smoothOK && !reduced()) { rail.scrollTo({ left: target, behavior: 'smooth' }); } else { rail.scrollLeft = target; }
      scheduleSettle();
      scheduleUI();
    }
    function currentIndex(s, mx) {
      var n = items.length;
      if (!n) return 0;
      if (mode !== 'loop' && mx > 1 && s >= mx - 1) return endIndex !== null ? Math.max(0, Math.min(n - 1, endIndex)) : n - 1;
      return nearestIndex(s);
    }
    function nearestIndex(s) {
      var n = items.length;
      if (!n) return 0;
      var rel = s + inset;
      var base = items[0].start;
      if (mode === 'loop' && loopWidth > 0) rel = base + ((((rel - base) % loopWidth) + loopWidth) % loopWidth);
      var best = 0, bestD = Infinity;
      for (var i = 0; i < n; i++) {
        var dist = Math.abs(items[i].start - rel);
        if (dist < bestD) { bestD = dist; best = i; }
      }
      if (mode === 'loop' && Math.abs(base + loopWidth - rel) < bestD) best = 0;
      return best;
    }
    function flushAnnounce() {
      if (!announce) return;
      announce = false;
      if (!controls.status.length) return;
      var text = String(currentIndex(rail.scrollLeft, maxScroll()) + 1);
      var total = String(items.length || cards.length);
      controls.status.forEach(function (el) {
        var tpl = el.getAttribute('data-ao-rail-status-template') || '[index] / [total]';
        el.textContent = tpl.replace('[index]', text).replace('[total]', total);
      });
    }
    function hasUI() {
      return contract || controls.prev.length || controls.next.length || controls.pause.length || controls.counters.length;
    }
    function scheduleUI() { if (!uiFrame && !destroyed && hasUI()) uiFrame = requestAnimationFrame(updateUI); }
    function updateUI() {
      uiFrame = 0;
      if (destroyed) return;
      var total = items.length || cards.length;
      var mx = maxScroll();
      var s = pendingTarget !== null ? pendingTarget : rail.scrollLeft;
      var loopMode = mode === 'loop';
      var overflow = loopMode || mx > 1;
      var atStart = !loopMode && s <= 1;
      var atEdge = !loopMode && s >= mx - 1;
      var index = total ? currentIndex(s, mx) : 0;
      // next stays enabled at the physical end until the counter has stepped to the last card
      var atEnd = atEdge && index >= (items.length || total) - 1;
      var motion = mode === 'loop' || mode === 'run' ? 'true' : 'false';
      var overflowValue = overflow ? 'true' : 'false';
      if (contract) {
        setAttr(rail, 'data-ao-rail-count', String(total));
        setAttr(rail, 'data-ao-rail-overflow', overflowValue);
        setAttr(rail, 'data-ao-rail-edge', !overflow ? 'none' : atStart ? 'end' : atEdge ? 'start' : 'both');
        setAttr(rail, 'data-ao-rail-motion', motion);
      }
      controls.prev.forEach(function (b) {
        setAttr(b, 'aria-disabled', overflow && !atStart ? 'false' : 'true');
        setAttr(b, 'data-ao-rail-overflow', overflowValue);
      });
      controls.next.forEach(function (b) {
        setAttr(b, 'aria-disabled', overflow && !atEnd ? 'false' : 'true');
        setAttr(b, 'data-ao-rail-overflow', overflowValue);
      });
      controls.pause.forEach(function (b) { setAttr(b, 'data-ao-rail-motion', motion); });
      controls.ui.forEach(function (u) {
        setAttr(u, 'data-ao-rail-overflow', overflowValue);
        setAttr(u, 'data-ao-rail-motion', motion);
      });
      controls.counters.forEach(function (c) {
        setAttr(c, 'data-ao-rail-overflow', overflowValue);
        c.querySelectorAll('[data-ao-rail-index]').forEach(function (el) { setText(el, pad2(index + 1)); });
        c.querySelectorAll('[data-ao-rail-total]').forEach(function (el) { setText(el, pad2(total)); });
      });
      if (index !== lastIndex) {
        lastIndex = index;
        emit(rail, 'ao:rail:change', { index: index + 1, total: total });
      }
    }
    function togglePause() {
      userPaused = !userPaused;
      controls.pause.forEach(function (b) {
        b.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        var label = b.getAttribute(userPaused ? 'data-ao-rail-label-play' : 'data-ao-rail-label-pause');
        if (label) b.setAttribute('aria-label', label);
      });
      if (userPaused) { stop(); } else { start(); }
    }
    function bindControls() {
      if (!rail.id) return;
      var id = String(rail.id).replace(/["\\]/g, '\\$&');
      function q(sel) { return toArray(document.querySelectorAll(sel)); }
      controls.prev = q('[data-ao-rail-prev][aria-controls="' + id + '"]');
      controls.next = q('[data-ao-rail-next][aria-controls="' + id + '"]');
      controls.pause = q('[data-ao-rail-pause][aria-controls="' + id + '"]');
      controls.counters = q('[data-ao-rail-for="' + id + '"]:not([data-ao-rail-status])');
      controls.status = q('[data-ao-rail-status][data-ao-rail-for="' + id + '"]');
      controls.ui = [];
      controls.prev.concat(controls.next, controls.pause, controls.counters, controls.status).forEach(function (el) {
        var box = el.closest ? el.closest('[data-ao-rail-ui]') : null;
        if (box && controls.ui.indexOf(box) < 0) controls.ui.push(box);
      });
      function bind(list, fn) {
        list.forEach(function (b) {
          on(b, 'click', function (e) {
            e.preventDefault();
            if (b.getAttribute('aria-disabled') === 'true') return;
            fn();
          });
        });
      }
      bind(controls.prev, function () { go(-1); });
      bind(controls.next, function () { go(1); });
      bind(controls.pause, togglePause);
      controls.prev.concat(controls.next, controls.pause).forEach(function (b) {
        on(b, 'focusin', function () { if (keyboardFocus(b)) { controlFocused = true; stop(); } });
        on(b, 'focusout', function () { controlFocused = false; start(); });
      });
    }

    /* ---------- desktop mouse drag ---------- */
    var dragId = null, dragX = 0, dragFrom = 0, snapInline = '';
    function endDrag() {
      dragging = false;
      rail.classList.remove('ao-rail--dragging');
      rail.style.scrollSnapType = snapInline;
    }
    function bindDrag() {
      on(rail, 'pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        dragId = e.pointerId;
        dragX = e.clientX;
        dragFrom = rail.scrollLeft;
      });
      on(rail, 'pointermove', function (e) {
        if (dragId === null || e.pointerId !== dragId) return;
        if (!(e.buttons & 1)) { dragId = null; if (dragging) { endDrag(); holdThenResume(2600); } return; }
        var dx = e.clientX - dragX;
        if (!dragging) {
          if (Math.abs(dx) < 6) return;
          dragging = true;
          stop();
          pendingTarget = null;
          navigating = false;
          snapInline = rail.style.scrollSnapType;
          rail.style.scrollSnapType = 'none';
          rail.classList.add('ao-rail--dragging');
          try { rail.setPointerCapture(dragId); } catch (err) { /* capture unsupported */ }
          try { var sel = window.getSelection && window.getSelection(); if (sel && sel.removeAllRanges) sel.removeAllRanges(); } catch (err) { /* ignore */ }
        }
        var target = dragFrom - dx;
        if (mode === 'loop' && loopWidth > 0) {
          var w = wrap(target);
          if (Math.abs(w - target) >= 0.5) { dragFrom += w - target; target = w; }
        }
        rail.scrollLeft = target;
        pos = rail.scrollLeft;
        if (e.cancelable) e.preventDefault();
      });
      function up(e) {
        if (dragId === null || e.pointerId !== dragId) return;
        dragId = null;
        if (!dragging) return;
        endDrag();
        try { rail.releasePointerCapture(e.pointerId); } catch (err) { /* already released */ }
        suppressClick = true;
        setTimeout(function () { suppressClick = false; }, 0);
        scheduleSettle();
        if (pauseInteraction) { holdThenResume(2600); } else { start(); }
      }
      on(rail, 'pointerup', up);
      on(rail, 'pointercancel', up);
      on(window, 'pointerup', function (e) { if (!dragging && dragId === e.pointerId) dragId = null; });
      on(rail, 'click', function (e) {
        if (!suppressClick) return;
        suppressClick = false;
        e.preventDefault();
        e.stopPropagation();
      }, true);
      on(rail, 'dragstart', function (e) { e.preventDefault(); });
    }

    /* ---------- wiring ---------- */
    toArray(rail.children).forEach(function (el) { if (el.hasAttribute('data-ao-marquee-clone')) rail.removeChild(el); });
    cards = originals();

    if (pauseHover) {
      on(rail, 'pointerenter', function (e) { if (e.pointerType === 'mouse') { hover = true; stop(); } });
      on(rail, 'pointerleave', function (e) { if (e.pointerType === 'mouse') { hover = false; start(); } });
      try { if (finePointer && finePointer.matches && rail.matches(':hover')) hover = true; } catch (e) { /* ignore */ }
    }
    on(rail, 'focusin', function (e) {
      if (pauseHover && keyboardFocus(e.target)) { focused = true; stop(); }
    });
    on(rail, 'focusout', function (e) {
      if (e.relatedTarget && rail.contains(e.relatedTarget)) return;
      focused = false;
      start();
      scheduleSettle();
    });
    if (pauseInteraction) {
      on(rail, 'pointerdown', function () { pointerHold = true; stop(); });
      var releasePointer = function () {
        if (!pointerHold) return;
        pointerHold = false;
        if (!touchHold) holdThenResume(2600);
      };
      on(window, 'pointerup', releasePointer);
      on(window, 'pointercancel', releasePointer);
      on(rail, 'touchstart', function () { touchHold = true; hover = false; stop(); }, { passive: true });
      var touchEnd = function (e) {
        if (e.touches && e.touches.length) return;
        touchHold = false;
        hover = false;
        holdThenResume(2600);
        scheduleSettle();
      };
      on(rail, 'touchend', touchEnd, { passive: true });
      on(rail, 'touchcancel', touchEnd, { passive: true });
      on(rail, 'wheel', function () { holdThenResume(2600); }, { passive: true });
    }
    if (drag) bindDrag();
    on(rail, 'scroll', onScroll, { passive: true });
    if ('onscrollend' in rail) {
      on(rail, 'scrollend', function () {
        if (navigating) return; // an instant loop jump fires scrollend before the smooth scroll that follows it ends
        clearTimeout(settleTimer);
        onSettle();
      });
    }
    on(rail, 'play', function () { playing = true; stop(); }, true);
    on(rail, 'pause', function () { playing = anyPlaying(); if (!playing) holdThenResume(1200); }, true);
    on(rail, 'ended', function () { playing = anyPlaying(); if (!playing) holdThenResume(1200); }, true);
    on(rail, 'load', function (e) { if (e.target && e.target.tagName === 'IMG') scheduleLayout(); }, true);
    on(rail, 'ao:marquee:hold', function () { holdThenResume(3200); centreLoop(); });
    on(document, 'visibilitychange', function () { if (document.hidden) { stop(); } else { start(); } });
    on(window, 'resize', scheduleLayout);
    if (reduce) {
      if (reduce.addEventListener) { on(reduce, 'change', scheduleLayout); }
      else if (reduce.addListener) { reduce.addListener(scheduleLayout); legacyMq = true; }
    }
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(function () { scheduleLayout(); });
      ro.observe(rail);
      cards.forEach(function (c) { ro.observe(c); });
    }
    bindControls();

    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { visible = entry.isIntersecting; if (visible) { start(); } else { stop(); } });
      }, { threshold: 0.05 });
      io.observe(rail);
    } else {
      visible = true;
    }

    layout();

    /* ---------- controller API (editor + window.aoRailEngine) ---------- */
    self.focus = function (card) {
      blockHold = true;
      clearTimeout(idleTimer);
      stop();
      var railRect = rail.getBoundingClientRect();
      var x = rail.scrollLeft + (card.getBoundingClientRect().left - railRect.left - rail.clientLeft) - inset;
      pendingTarget = null;
      endIndex = null;
      if (mode !== 'loop') {
        for (var k = 0; k < items.length; k++) { if (items[k].el === card) { endIndex = k; break; } }
      }
      rail.scrollLeft = Math.max(0, Math.min(maxScroll(), x));
      pos = rail.scrollLeft;
      scheduleUI();
    };
    self.release = function () { blockHold = false; start(); scheduleSettle(); };
    self.holdSection = function () { sectionHold = true; stop(); };
    self.releaseSection = function () { sectionHold = false; start(); };
    self.hold = holdThenResume;
    self.next = function () { go(1); };
    self.prev = function () { go(-1); };
    self.refresh = scheduleLayout;
    self.state = function () {
      return {
        mode: mode, fits: fits, running: running, loopWidth: loopWidth, sets: sets.length,
        count: items.length || cards.length, index: (items.length ? currentIndex(rail.scrollLeft, maxScroll()) : 0) + 1,
        scrollLeft: rail.scrollLeft, maxScroll: maxScroll(), listeners: listeners.length,
        holds: {
          visible: visible, hover: hover, focused: focused, controlFocused: controlFocused, pointerHold: pointerHold,
          touchHold: touchHold, dragging: dragging, playing: playing, userHold: userHold, blockHold: blockHold,
          sectionHold: sectionHold, userPaused: userPaused, finished: finished
        }
      };
    };
    self.destroy = function (restore) {
      if (destroyed) return;
      destroyed = true;
      stop();
      clearTimeout(idleTimer);
      clearTimeout(settleTimer);
      if (layoutFrame) cancelAnimationFrame(layoutFrame);
      if (uiFrame) cancelAnimationFrame(uiFrame);
      layoutFrame = uiFrame = 0;
      listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
      listeners = [];
      if (legacyMq && reduce && reduce.removeListener) reduce.removeListener(scheduleLayout);
      if (io) io.disconnect();
      if (ro) ro.disconnect();
      if (dragging) endDrag();
      var k = live.indexOf(self);
      if (k > -1) live.splice(k, 1);
      if (restore) {
        removeSets(0);
        rail.classList.remove('ao-marquee', 'ao-marquee--fits', 'ao-marquee--static', 'ao-rail--dragging');
        ['data-ao-rail-overflow', 'data-ao-rail-edge', 'data-ao-rail-motion'].forEach(function (a) { rail.removeAttribute(a); });
        delete rail.dataset.aoMarqueeReady;
      }
    };
  }

  function init(rail) {
    if (!rail || rail.nodeType !== 1 || rail.dataset.aoMarqueeReady) return;
    if (rail.closest && rail.closest('[data-ao-marquee-clone]')) return;
    rail.dataset.aoMarqueeReady = '1';
    var c = new Controller(rail);
    if (controllers) controllers.set(rail, c);
    live.push(c);
  }
  function forget(rail) {
    var c = controllers && controllers.get(rail);
    if (!c) return;
    c.destroy();
    controllers.delete(rail);
  }
  // Building a Controller clones the rail's cards so the marquee can loop, which on the homepage inserted
  // 22 deep subtrees into the document before first paint for two rails that are thousands of pixels below
  // the fold, and measured each one. Rails are now built when they come within two viewports instead. At
  // that distance the cards, their controls and the ao:marquee:ready event are all in place long before the
  // customer can see or reach them, and nothing about the markup Google reads changes: the authentic cards
  // are server-rendered, only the duplicate loop copies are deferred.
  var railWatcher = null;
  function watch(rail) {
    if (!rail || rail.nodeType !== 1 || rail.dataset.aoMarqueeReady) return;
    if (!('IntersectionObserver' in window) || (window.Shopify && window.Shopify.designMode)) { init(rail); return; }
    if (!railWatcher) {
      railWatcher = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          railWatcher.unobserve(entry.target);
          init(entry.target);
        });
      }, { rootMargin: '200% 0px 200% 0px' });
    }
    railWatcher.observe(rail);
  }
  function boot() {
    live.slice().forEach(function (c) { if (!document.documentElement.contains(c.rail)) forget(c.rail); });
    document.querySelectorAll(RAILS).forEach(watch);
  }

  function railCard(node) {
    var rail = node && node.closest ? node.closest(RAILS) : null;
    if (!rail) return null;
    var card = node;
    while (card && card.parentElement && card.parentElement !== rail) card = card.parentElement;
    if (!card || card.parentElement !== rail || card.hasAttribute('data-ao-marquee-clone')) return null;
    return { rail: rail, card: card };
  }
  function eachRailIn(event, fn) {
    if (!controllers || !event.target || !event.target.querySelectorAll) return;
    event.target.querySelectorAll(RAILS).forEach(function (rail) {
      var c = controllers.get(rail);
      if (c) fn(c, rail);
    });
  }

  window.aoRailEngine = {
    version: 2,
    boot: boot,
    get: function (rail) { return controllers ? controllers.get(rail) || null : null; },
    refresh: function (rail) { var c = controllers && controllers.get(rail); if (c) c.refresh(); },
    reset: function (rail) {
      var c = controllers && controllers.get(rail);
      if (c) { c.destroy(true); controllers.delete(rail); }
      init(rail);
    }
  };

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); } else { boot(); }
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', function (event) {
    eachRailIn(event, function (c, rail) { forget(rail); });
  });
  document.addEventListener('shopify:section:select', function (event) {
    eachRailIn(event, function (c) { c.holdSection(); });
  });
  document.addEventListener('shopify:section:deselect', function (event) {
    eachRailIn(event, function (c) { c.releaseSection(); });
  });
  document.addEventListener('shopify:block:select', function (event) {
    if (!controllers) return;
    var hit = railCard(event.target);
    var c = hit && controllers.get(hit.rail);
    if (c) c.focus(hit.card);
  });
  document.addEventListener('shopify:block:deselect', function (event) {
    if (!controllers) return;
    var hit = railCard(event.target);
    var c = hit && controllers.get(hit.rail);
    if (c) c.release();
  });
})();
